/* Routinae — welcome signup popup behaviour.
 *
 * Loaded only when snippets/routinae-popup.liquid renders, which is only
 * when Theme settings -> Promotions enables the popup. Everything here is
 * additive and self-contained: no globals exported, no dependency beyond
 * the markup the snippet renders.
 *
 * Rules implemented (see the snippet header for the full contract):
 *   - open after a delay OR on desktop exit-intent, whichever fires first
 *   - at most once per session; a dismissal holds for N days
 *   - never while the cookie banner (#rt-consent) is open — re-checks
 *     every few seconds until the banner is answered
 *   - after a form round-trip (#rt-popup in the URL) reopen immediately
 *     to show the success or error state
 *   - storage behind try/catch: with storage blocked the popup can only
 *     fall back to "show this pageload", never to a nag loop
 *   - dialog semantics: focus trap, Esc closes, focus restored on close
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-rt-popup]');
  if (!root) return;

  var dialog = root.querySelector('[data-rt-popup-dialog]');
  var overlay = root.querySelector('[data-rt-popup-overlay]');
  var form = root.querySelector('form');
  var stateEl = root.querySelector('[data-rt-popup-state]');
  var state = stateEl ? stateEl.getAttribute('data-rt-popup-state') : null;

  var delayMs = (parseInt(root.getAttribute('data-rt-popup-delay'), 10) || 10) * 1000;
  var dismissDays = parseInt(root.getAttribute('data-rt-popup-dismiss-days'), 10) || 30;

  var LS_DISMISSED = 'rt-popup-dismissed-at';
  var LS_DONE = 'rt-popup-done';
  var SS_SHOWN = 'rt-popup-shown';

  function lsGet(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } }
  function lsSet(key, value) { try { window.localStorage.setItem(key, value); } catch (e) { /* fine */ } }
  function ssGet(key) { try { return window.sessionStorage.getItem(key); } catch (e) { return null; } }
  function ssSet(key, value) { try { window.sessionStorage.setItem(key, value); } catch (e) { /* fine */ } }

  // 26 Oct 2026: "open your gift" stage in front of the form. Always the same EUR 5 welcome gift,
  // nothing random. Skipped after a form error and once opened in this session.
  var gift = root.querySelector('[data-rt-gift]');
  var reveal = root.querySelector('[data-rt-gift-reveal]');
  var giftBtn = root.querySelector('[data-rt-gift-open]');
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (gift && reveal && giftBtn && state !== 'error' && !ssGet('rt-gift-open')) {
    gift.hidden = false;
    reveal.hidden = true;
    giftBtn.addEventListener('click', function () {
      giftBtn.setAttribute('aria-expanded', 'true');
      ssSet('rt-gift-open', '1');
      gift.classList.add('is-opening');
      window.setTimeout(function () {
        gift.hidden = true;
        reveal.hidden = false;
        reveal.classList.add('is-revealed');
        var email = reveal.querySelector('input[type="email"]');
        if (email) email.focus();
      }, reduceMotion ? 0 : 2000);
    });
  }

  var opened = false;
  var triggered = false;
  var previousFocus = null;

  function consentBannerOpen() {
    var banner = document.getElementById('rt-consent');
    if (banner && !banner.hidden) return true;
    // Shopify's own cookie banner (customer privacy) - never stack on it either
    var shop = document.getElementById('shopify-pc__banner');
    return !!(shop && shop.offsetParent !== null);
  }

  function focusables() {
    var nodes = dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(nodes, function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = focusables();
    if (items.length === 0) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function open() {
    if (opened) return;
    opened = true;
    previousFocus = document.activeElement;
    root.hidden = false;
    ssSet(SS_SHOWN, '1');
    document.addEventListener('keydown', onKeydown);

    var target =
      dialog.querySelector('[data-rt-popup-state="success"]') ||
      (gift && !gift.hidden ? giftBtn : null) ||
      dialog.querySelector('input[type="email"]') ||
      dialog.querySelector('[data-rt-popup-close]');
    if (target) target.focus();
  }

  function close() {
    if (!opened) return;
    opened = false;
    root.hidden = true;
    document.removeEventListener('keydown', onKeydown);
    // A plain dismissal sleeps for N days. A completed signup (success
    // state) never comes back: LS_DONE is set below, on open.
    lsSet(LS_DISMISSED, String(Date.now()));
    if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
  }

  // copy the welcome code (success screen)
  root.querySelectorAll('[data-rt-copy]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var el = document.getElementById(btn.getAttribute('data-rt-copy'));
      if (!el) return;
      var label = btn.textContent;
      function done(text) { btn.textContent = text; window.setTimeout(function () { btn.textContent = label; }, 1600); }
      function selectIt() { var r = document.createRange(); r.selectNodeContents(el); var s = window.getSelection(); s.removeAllRanges(); s.addRange(r); }
      try {
        navigator.clipboard.writeText(el.textContent.trim()).then(function () { done(btn.getAttribute('data-rt-copied') || label); }, selectIt);
      } catch (e) { selectIt(); }
    });
  });

  root.querySelectorAll('[data-rt-popup-close]').forEach(function (btn) {
    btn.addEventListener('click', close);
  });
  if (overlay) overlay.addEventListener('click', close);

  // Belt and braces beside the checkbox's own `required`: never post
  // without the consent box ticked (see GOTCHAS.md #21 for why presence
  // of `required` alone is not trusted in this project).
  if (form) {
    form.addEventListener('submit', function (event) {
      var consent = form.querySelector('[data-rt-popup-consent]');
      if (consent && !consent.checked) {
        event.preventDefault();
        if (typeof form.reportValidity === 'function') form.reportValidity();
      }
    });
  }

  // --- form round-trip: reopen at once to show the result ------------------
  if (state && window.location.hash.indexOf('rt-popup') !== -1) {
    if (state === 'success') lsSet(LS_DONE, '1');
    open();
    return; // no timers, no exit-intent, on this pageload
  }

  // --- normal arming --------------------------------------------------------
  if (lsGet(LS_DONE)) return;

  var dismissedAt = parseInt(lsGet(LS_DISMISSED), 10);
  if (dismissedAt && Date.now() - dismissedAt < dismissDays * 24 * 60 * 60 * 1000) return;

  if (ssGet(SS_SHOWN)) return;

  function tryOpen() {
    if (triggered || opened) return;
    if (consentBannerOpen()) {
      // The cookie banner is on screen. Never stack on it — try again
      // shortly; the visitor answers it exactly once.
      window.setTimeout(tryOpen, 4000);
      return;
    }
    triggered = true;
    open();
  }

  // 24 Oct 2026: second page viewed, or 15 s on a computer; phones only from the second page.
  // Visitors who arrive from one of our emails never see it in that visit.
  if (/[?&]utm_medium=email/i.test(window.location.search)) ssSet('rt-popup-from-email', '1');
  if (ssGet('rt-popup-from-email')) return;
  var pageViews = (parseInt(ssGet('rt-popup-pv'), 10) || 0) + 1;
  ssSet('rt-popup-pv', String(pageViews));
  var finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
  var loadedAt = Date.now();
  if (pageViews >= 2) {
    window.setTimeout(tryOpen, 2500);
  } else if (finePointer) {
    window.setTimeout(tryOpen, delayMs);
  }
  if (finePointer) {
    document.addEventListener('mouseout', function onExitIntent(event) {
      if (event.relatedTarget || event.clientY > 0) return;
      if (Date.now() - loadedAt < 5000) return;
      document.removeEventListener('mouseout', onExitIntent);
      tryOpen();
    });
  }
})();
